import React, { useState, useEffect } from 'react';
import { X, Phone, Mail, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { getGeoLocation, submitLead, getCountryFromDialCode } from '../services/api';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: '',
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = [
    { code: '+1', country: 'US/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+61', country: 'Australia' },
    { code: '+971', country: 'UAE' },
    { code: '+92', country: 'Pakistan' },
    { code: '+31', country: 'Netherlands' },
    { code: '+34', country: 'Spain' },
    { code: '+55', country: 'Brazil' },
    { code: '+52', country: 'Mexico' },
  ];

  // Fetch geolocation data when modal opens
  useEffect(() => {
    if (isOpen && !geoData) {
      const fetchGeoData = async () => {
        const result = await getGeoLocation();
        if (result.success) {
          setGeoData(result.data);
          // Auto-set country code based on geolocation
          const dialCode = `+${result.data.countryCode === 'US' ? '1' : result.data.countryCode === 'GB' ? '44' : result.data.countryCode === 'IN' ? '91' : '1'}`;
          setFormData(prev => ({ ...prev, countryCode: dialCode }));
        }
      };
      fetchGeoData();
    }
  }, [isOpen, geoData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number - only digits
    if (!/^\d{7,15}$/.test(formData.phone)) {
      toast.error('Phone number must contain only digits (7-15 digits)');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit lead with geolocation data
      const result = await submitLead(formData, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! We will contact you soon.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          phone: '',
          message: '',
        });
        
        // Close modal
        onClose();
        
        // Redirect to thank you page if customerId is present
        if (result.customerId) {
          window.location.href = '/thanks';
        }
      } else {
        // Show error from API
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden relative animate-in fade-in zoom-in duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 text-gray-600 hover:text-gray-800 transition-colors bg-white rounded-full p-1.5 sm:p-2 shadow-lg hover:shadow-xl"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        <div className="grid lg:grid-cols-2 h-full max-h-[95vh] sm:max-h-[90vh]">
          {/* Left Side - Image & Info (Hidden on mobile, shown on tablet+) */}
          <div className="hidden lg:flex relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 p-8 xl:p-12 flex-col justify-between text-white overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-300 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
            </div>

            <div className="relative z-10">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🎯 Expert Legal Guidance
              </div>
              
              <h2 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight">
                Get Expert Legal Help Today!
              </h2>
              
              <p className="text-blue-100 text-base xl:text-lg mb-8">
                Don't wait! Connect with our expert team now and get personalized legal solutions tailored to your needs. Fast response guaranteed.
              </p>

              {/* Features */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 mt-1">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">24/7 Expert Support</h4>
                    <p className="text-sm text-blue-100">Our team is always ready to help you</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 mt-1">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Fast Response Time</h4>
                    <p className="text-sm text-blue-100">Get replies within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 mt-1">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Trusted by 100K+ Clients</h4>
                    <p className="text-sm text-blue-100">Join thousands of satisfied customers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-4 sm:p-6 lg:p-8 xl:p-12 bg-gray-50 overflow-y-auto">
            {/* Mobile header with gradient */}
            <div className="lg:hidden mb-6 -mx-4 -mt-4 sm:-mx-6 sm:-mt-6 p-4 sm:p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-xl sm:rounded-t-2xl">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold mb-3">
                🎯 Expert Legal Guidance
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-1">Get Expert Legal Help Today!</h3>
              <p className="text-blue-100 text-sm">Fast response guaranteed</p>
            </div>

            {/* Desktop header */}
            <div className="hidden lg:block mb-6 xl:mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Start Your Legal Journey</h3>
              <p className="text-gray-600">Fill out the form and our experts will respond within 24 hours</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 sm:h-12 text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="pl-10 sm:pl-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 sm:h-12 text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Phone Number *
                </label>
                <div className="flex space-x-2">
                  <Select
                    value={formData.countryCode}
                    onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                  >
                    <SelectTrigger className="w-24 sm:w-32 border-gray-300 h-11 sm:h-12 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code}>
                          {item.code} {item.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="1234567890"
                      maxLength={10}
                      className="pl-10 sm:pl-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 sm:h-12 text-base"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Your Message *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your legal needs..."
                    rows={4}
                    className="pl-10 sm:pl-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500 resize-none text-base min-h-[100px]"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-5 sm:py-6 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform active:scale-[0.98] sm:hover:scale-[1.02] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Send Message'}
              </Button>

              <p className="text-xs text-center text-gray-500 mt-3 sm:mt-4 px-2">
                By submitting this form, you agree to our privacy policy
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;